import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { pad2, parseTime, time12, toStored } from "./time12";

// Our own time box, because the browser's <input type="time"> shows 12 or 24 hours depending on
// each PC's Windows settings and a page cannot override it. Always 12-hour, set on a clock face
// or typed. value and onChange use the stored 24-hour "HH:MM" ("" = no time).

const DIAL_SIZE = 248;
const CENTER = DIAL_SIZE / 2;
const LABEL_RADIUS = 96;
const HOUR_LABELS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTE_LABELS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

const validHour = (text) => Number(text) >= 1 && Number(text) <= 12;

const pointAt = (degrees) => {
  const rad = (degrees * Math.PI) / 180;
  return { x: CENTER + LABEL_RADIUS * Math.sin(rad), y: CENTER - LABEL_RADIUS * Math.cos(rad) };
};

function ClockPanel({ label, value, defaultPeriod, clearable, onSet, onClose }) {
  const start = parseTime(value);
  const [hourText, setHourText] = useState(start ? pad2(start.hour) : "");
  const [minuteText, setMinuteText] = useState(start ? pad2(start.minute) : "");
  const [period, setPeriod] = useState(start ? start.period : defaultPeriod);
  const [mode, setMode] = useState("hour");
  const panelRef = useRef(null);
  const dialRef = useRef(null);
  const minuteRef = useRef(null);
  const dragging = useRef(false);

  // the panel takes the focus, not the hour box, so a phone does not open its keyboard over the clock
  useEffect(() => { if (panelRef.current) panelRef.current.focus(); }, []);

  const hour = Number(hourText);
  const minute = Number(minuteText);
  const hourOk = validHour(hourText);
  const minuteOk = minute <= 59;
  const canSet = hourOk && minuteOk;
  const problem = hourText !== "" && !hourOk ? "Hour must be 1 to 12." : !minuteOk ? "Minutes must be 0 to 59." : "";

  const handDegrees = mode === "hour" ? (hourOk ? hour * 30 : null) : (minuteOk ? minute * 6 : null);
  const onLabel = mode === "hour" || minute % 5 === 0;
  const tip = handDegrees === null ? null : pointAt(handDegrees);

  const pickFromPointer = (ev) => {
    const box = dialRef.current.getBoundingClientRect();
    const dx = ev.clientX - (box.left + box.width / 2);
    const dy = ev.clientY - (box.top + box.height / 2);
    const degrees = ((Math.atan2(dx, -dy) * 180) / Math.PI + 360) % 360;
    if (mode === "hour") setHourText(pad2(Math.round(degrees / 30) % 12 || 12));
    else setMinuteText(pad2(Math.round(degrees / 6) % 60));
  };

  const set = () => { if (canSet) onSet(toStored(hour, minute, period)); };

  const readoutCls = (active, ok) =>
    `w-14 h-12 rounded-lg text-center font-mono text-2xl font-semibold border focus:outline-none focus:ring-2 focus:ring-indigo-300 ${
      !ok ? "border-rose-400 bg-rose-50 text-rose-700" : active ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-slate-50 text-slate-700"
    }`;

  return (
    <div
      className="fixed inset-0 z-[110] bg-slate-900/40 flex items-center justify-center p-4"
      onClick={(ev) => { ev.stopPropagation(); onClose(); }}
      onKeyDown={(ev) => { if (ev.key === "Escape") { ev.stopPropagation(); onClose(); } }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`Set ${label} time`}
        className="bg-white rounded-2xl shadow-xl w-full max-w-xs max-h-[90vh] overflow-y-auto outline-none"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="px-5 pt-4 text-sm font-bold text-slate-800">Set {label} time</div>

        <div className="px-5 pt-3 flex items-center justify-center gap-2">
          <input
            aria-label="Hour, 1 to 12"
            inputMode="numeric"
            placeholder="--"
            value={hourText}
            className={readoutCls(mode === "hour", hourText === "" || hourOk)}
            onFocus={(ev) => { setMode("hour"); ev.target.select(); }}
            onClick={(ev) => ev.target.select()}
            onChange={(ev) => {
              const text = ev.target.value.replace(/\D/g, "").slice(0, 2);
              setHourText(text);
              if (text.length === 2 && validHour(text) && minuteRef.current) minuteRef.current.focus();
            }}
            onBlur={() => setHourText((t) => (validHour(t) ? pad2(Number(t)) : t))}
            onKeyDown={(ev) => { if (ev.key === "Enter") set(); }}
          />
          <span className="text-2xl font-semibold text-slate-400">:</span>
          <input
            ref={minuteRef}
            aria-label="Minutes, 0 to 59"
            inputMode="numeric"
            placeholder="--"
            value={minuteText}
            className={readoutCls(mode === "minute", minuteOk)}
            onFocus={(ev) => { setMode("minute"); ev.target.select(); }}
            onClick={(ev) => ev.target.select()}
            onChange={(ev) => setMinuteText(ev.target.value.replace(/\D/g, "").slice(0, 2))}
            onBlur={() => setMinuteText((t) => (t !== "" && Number(t) <= 59 ? pad2(Number(t)) : t))}
            onKeyDown={(ev) => { if (ev.key === "Enter") set(); }}
          />
          <div className="ml-1 flex flex-col rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
            {["AM", "PM"].map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={period === p}
                onClick={() => setPeriod(p)}
                className={`px-3 h-6 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-300 ${period === p ? "bg-indigo-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 pt-2 text-center text-xs text-slate-500" aria-live="polite">
          {problem ? <span className="font-semibold text-rose-600">{problem}</span> : mode === "hour" ? "Choose the hour" : "Choose the minutes"}
        </div>

        {/* Mouse and touch only: the hour and minute boxes above do the same job from the keyboard */}
        <div className="px-5 pt-2 flex justify-center">
          <div
            ref={dialRef}
            aria-hidden="true"
            className="relative rounded-full bg-slate-100 cursor-pointer select-none"
            style={{ width: DIAL_SIZE, height: DIAL_SIZE, touchAction: "none" }}
            onPointerDown={(ev) => {
              dragging.current = true;
              ev.currentTarget.setPointerCapture(ev.pointerId);
              pickFromPointer(ev);
            }}
            onPointerMove={(ev) => { if (dragging.current) pickFromPointer(ev); }}
            onPointerUp={() => {
              if (dragging.current && mode === "hour") setMode("minute");
              dragging.current = false;
            }}
            onPointerCancel={() => { dragging.current = false; }}
          >
            <svg width={DIAL_SIZE} height={DIAL_SIZE} className="absolute inset-0 pointer-events-none text-indigo-600">
              <circle cx={CENTER} cy={CENTER} r="3" fill="currentColor" />
              {tip && (
                <>
                  <line x1={CENTER} y1={CENTER} x2={tip.x} y2={tip.y} stroke="currentColor" strokeWidth="2" />
                  <circle cx={tip.x} cy={tip.y} r={onLabel ? 18 : 7} fill="currentColor" />
                </>
              )}
            </svg>
            {(mode === "hour" ? HOUR_LABELS : MINUTE_LABELS).map((n) => {
              const at = pointAt(mode === "hour" ? n * 30 : n * 6);
              const selected = mode === "hour" ? n === hour : minuteOk && n === minute;
              return (
                <span
                  key={n}
                  className={`absolute pointer-events-none text-sm font-semibold ${selected ? "text-white" : "text-slate-700"}`}
                  style={{ left: at.x, top: at.y, transform: "translate(-50%, -50%)" }}
                >
                  {mode === "hour" ? n : pad2(n)}
                </span>
              );
            })}
          </div>
        </div>

        <div className="px-5 py-4 flex items-center gap-2">
          {clearable && value && (
            <button type="button" onClick={() => onSet("")} className="px-2 py-2 rounded-lg text-sm font-semibold text-rose-600 hover:bg-rose-50">Clear</button>
          )}
          <button type="button" onClick={onClose} className="ml-auto px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={set} disabled={!canSet} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-40">Set</button>
        </div>
      </div>
    </div>
  );
}

// defaultPeriod: which of AM / PM is chosen when the box starts empty.
// clearable: false when the time is required.
export default function ClockTimePicker({ label, value, onChange, defaultPeriod = "AM", clearable = true }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const close = () => {
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-label={`${label} time: ${value ? time12(value) : "not set"}. Open clock`}
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between gap-1 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-300"
      >
        <span className={`font-mono whitespace-nowrap ${value ? "text-slate-800" : "text-slate-400"}`}>{value ? time12(value) : "--:-- --"}</span>
        <i className="far fa-clock text-slate-500" aria-hidden="true"></i>
      </button>
      {open && createPortal(
        <ClockPanel
          label={label}
          value={value}
          defaultPeriod={defaultPeriod}
          clearable={clearable}
          onClose={close}
          onSet={(next) => { onChange(next); close(); }}
        />,
        document.body
      )}
    </>
  );
}
